import moment from 'moment';
import { ReachAPI } from '../../ReachApi';
import { PostType, SocialMedallionColors, Vendors } from './enums';
import {
    FacebookPost,
    InstagramBusinessPost,
    InstagramPost,
    RawFacebookPost,
    RawInstagramBusinessPost,
    RawInstagramPost,
    RawSlackPost,
    RawTwitterPost,
    RawWorkplacePost,
    SlackPost,
    TwitterPost,
    WorkplacePost,
} from './types';

/**
 * Truncates a string to the specified length.
 * Adds epsilon at the end of the string if it is longer than max length.
 */
export function truncateString(text: string, maxLength: number): string {
    if (text.length > maxLength) {
        return text.substring(0, maxLength) + '...';
    }
    return text;
}

/**
 * Rounds a number to the specified number of decimal places.
 */
export function round(value: number, precision: number): number {
    const multiplier = Math.pow(10, precision || 0);
    return Math.round(value * multiplier) / multiplier;
}

/**
 * Convert military time to 12 hour time with am/pm
 */
export function convertMilitaryTimeToStandard(time: string): string {
    const hours24 = parseInt(time.substring(0, 2), 10);

    const hours = ((hours24 + 11) % 12) + 1;
    const amPm = hours24 > 11 ? 'pm' : 'am';
    const minutes = time.substring(2);

    return `${hours}:${minutes}${amPm}`;
}

/**
 * Use date object to convert to either date or time depending on type parameter
 */
export function convertToDateOrTime(date: Date, type: 'date' | 'time'): string {
    const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    if (type === 'date') {
        //return date
        const dayofweek = date.getDay();
        const day = date.getDate();
        const monthIndex = date.getMonth();
        const year = date.getFullYear();
        return `${dayNames[dayofweek]}, ${monthNames[monthIndex]} ${day}, ${year}`;
    } else {
        //return time
        const hour = date.getHours() < 10 ? `0${date.getHours()}` : date.getHours();
        const minutes = date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes();

        return convertMilitaryTimeToStandard(hour.toString() + minutes.toString());
    }
}

/**
 * Find the array of items from the second argument
 * that are not in the first argument
 */
export function arrayDifference<T extends number | string>(array1: T[], array2: T[]): T[] {
    return array2.filter((item) => !array1.includes(item));
}

export function createFacebookPost(
    post: RawFacebookPost,
    profileId: string,
    reachProfileId: string,
    templateId: string
): FacebookPost {
    let resource: string | null;
    switch (post.type) {
        case PostType.Photo:
        case PostType.Image:
            resource =
                ReachAPI.server +
                '/socialmedia/facebook/image?object_id=' +
                String(post.object_id) +
                '&profile_id=' +
                profileId +
                '&reach_profile_id=' +
                reachProfileId;
            break;
        case PostType.Video:
            resource =
                post.object_id !== null
                    ? 'http://graph.facebook.com/' + post.object_id + '/picture/?type=normal'
                    : post.picture;
            break;
        default:
            resource = post.picture;
            break;
    }
    const result: FacebookPost = {
        facebook: true,
        date: post.created_time,
        formattedDate: moment(post.created_time).format('ddd, MMM D, YYYY | h:mma'),
        id: post.post_id,
        posttype: post.type,
        image: resource,
        message: post.message || post.description || post.caption,
        profilepic:
            ReachAPI.server +
            '/socialmedia/facebook/image?object_id=' +
            post.from_facebook_id +
            '&profile_id=' +
            profileId +
            '&reach_profile_id=' +
            reachProfileId,
        sm: Vendors.Facebook,
        template: templateId,
        xtramedia: null,
        by: post.from_name,
        color: SocialMedallionColors.Facebook,
    };

    if (result.message && result.image) {
        result.posttype = PostType.Photo;
    } else if (result.image) {
        result.posttype = PostType.Photo;
    } else if (result.message) {
        result.posttype = PostType.Text;
    }

    return result;
}

export function createTwitterPost(post: RawTwitterPost, templateId: string): TwitterPost {
    return {
        twitter: true,
        date: post.created_at,
        formattedDate: moment(post.created_at).format('ddd, MMM D, YYYY | h:mma'),
        id: post.id.toString(),
        image: post.image_url,
        message: post.text,
        profilepic:
            post.profile_image_url && post.profile_image_url !== null ? post.profile_image_url : '',
        posttype: PostType.Text,
        sm: Vendors.Twitter,
        template: templateId,
        xtramedia: null,
        by: post.handle,
        color: SocialMedallionColors.Twitter,
        mediaUrls: post.mediaUrls,
        mediaIds: post.mediaIds,
    };
}

export function createInstagramPost(post: RawInstagramPost, templateId: string): InstagramPost {
    return {
        instagram: true,
        date: post.created,
        formattedDate: moment(post.created).format('ddd, MMM D, YYYY | h:mma'),
        id: post.internal_id,
        image: post.image_url,
        message: post.caption_text,
        profilepic: post.profile_picture,
        posttype: PostType.Photo,
        sm: Vendors.Instagram,
        template: templateId,
        xtramedia: null,
        by: post.profile_username,
        color: SocialMedallionColors.Instagram,
    };
}

export function createInstagramBusinessPost(
    post: RawInstagramBusinessPost,
    templateId: string
): InstagramBusinessPost {
    return {
        instagram: true,
        date: post.created,
        formattedDate: moment(post.created).format('ddd, MMM D, YYYY | h:mma'),
        id: post.id,
        image: post.media_url,
        message: post.caption,
        profilepic: null,
        posttype: PostType.Photo,
        // use Instagram because the template is same for both Instagram and Instagram Business
        sm: Vendors.Instagram,
        template: templateId,
        xtramedia: null,
        by: post.username,
        color: SocialMedallionColors.InstagramBusiness,
    };
}

export function createWorkplacePost(post: RawWorkplacePost, templateId: string): WorkplacePost {
    return {
        workplace: true,
        date: post.created_time,
        formattedDate: moment(post.created_time).format('ddd, MMM D, YYYY | h:mma'),
        id: post.post_id,
        posttype: post.type,
        image: post.object_id
            ? ReachAPI.server +
              '/socialmedia/workplace/image?' +
              'profile_id=' +
              post.group_id +
              '&object_id=' +
              post.object_id
            : null,
        message: post.message,
        profilepic:
            ReachAPI.server +
            '/socialmedia/workplace/image?object_id=' +
            post.from_id +
            '&profile_id=' +
            post.group_id,
        sm: Vendors.Workplace,
        template: templateId,
        xtramedia: null,
        by: post.from_name,
        color: SocialMedallionColors.Workplace,
    };
}

export function createSlackPost(post: RawSlackPost, templateId: string): SlackPost {
    return {
        slack: true,
        date: post.date,
        formattedDate: moment(post.date).format('ddd, MMM D, YYYY | h:mma'),
        id: post.id.toString(),
        posttype: post.type,
        image: post.resource_url,
        message: post.comment || post.resource_title || post.message,
        profilepic: post.profile_picture,
        sm: Vendors.Slack,
        template: templateId,
        xtramedia: null,
        by: post.name,
        color: SocialMedallionColors.Slack,
    };
}

/**
 * Get all sibling nodes of the given node
 */
export function getSiblingNodes(node: HTMLElement): HTMLElement[] {
    const parent = node.parentElement;
    if (!parent) {
        return [];
    }
    return Array.from(parent.children).filter((child) => child !== node) as HTMLElement[];
}

/**
 * Wrap an element with another HTMLElement
 * replaces jquery wrap
 */
export function wrap(element: HTMLDivElement, wrapper: HTMLElement): void {
    element.parentNode?.insertBefore(wrapper, element);
    wrapper.appendChild(element);
}
